import { useState, type ReactNode, type MouseEvent, type KeyboardEvent } from 'react'
import { twMerge } from 'tailwind-merge'
import { Cell, Button, useKeyboardInteraction } from '@highlight/ui'
import { X } from 'lucide-react'
import { getActionIcon } from '@renderer/overlay/Sidepanel/utils/activity-helpers'
import { InvokeIcon } from '@renderer/components/invoke-icon'
import { ActionIconSlot } from '@renderer/components/action-icon-slot'
import { ActionWarning } from '@renderer/components/action-warning'
import { CopyIconButton } from '@renderer/overlay/components/copy-icon-button'
import { ActionTagArea } from '@renderer/components/action-tags'
import { applyMetadataTransitions, visibleNestedViews } from '@renderer/components/action-tags/status-transition'
import { TmuxInstallHint, isTmuxUnavailableError } from '@renderer/components/tmux-install-hint'
import { ActionContentPreview } from '@renderer/components/action-content-preview'
import { CommenterMeta, hasCommenterMeta } from '@renderer/components/commenter-meta'
import { NestedView } from '@renderer/components/nested-view'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer'
import { stagedActionEventProperties } from '@renderer/overlay/Sidepanel/hooks/staged-actions/track-staged-action'
import type {
  NestedActionView,
  StagedActionPrerequisite,
  StagedActionViewContentMeta,
} from '@renderer/overlay/Sidepanel/hooks/use-staged-actions'
import type { StagedActionAttachment } from './staged-action-image'
import {
  PrerequisiteCtaButton,
  useConnectionPrerequisite,
} from '@renderer/overlay/Sidepanel/hooks/staged-actions/use-connection-prerequisite'
import {
  isReportToUserStagedAction,
  getReportOriginSkill,
  OriginSkillIcon,
  ReportExpandToggle,
} from '@renderer/chat/integrations/report-to-user'
import { useClampedOverflow } from './use-clamped-overflow'

function isUrl(value: string): boolean {
  return value.startsWith('http://') || value.startsWith('https://')
}

export type ActionStatus = 'staged' | 'pending' | 'error' | 'undo-error'

export interface StagedActionTag {
  key: string
  value: string
  icon?: string
}

export interface StagedActionButton {
  label: string
  link: string
}

export interface StagedActionCellProps {
  stagedAction: {
    id: string
    actionType: 'sendSlackMessage' | 'createLinearTicket' | 'createNotionPage' | (string & NonNullable<unknown>)
    title: string
    description: string
    createdAt?: string
    toolSet?: string
    toolName?: string
    metadata?: Record<string, string>
    sourceMetadata?: { originSkill?: string }
    prerequisites?: Array<StagedActionPrerequisite>
    view?: {
      title: string
      icon: string
      icons?: Array<string>
      content?: string
      contentMeta?: StagedActionViewContentMeta
      tags?: Array<StagedActionTag>
      tagOrder?: Array<string>
      buttons?: Array<StagedActionButton>
      invokeLabel?: string
      invokeIcon?: string
      invokeWarning?: string
      invokeLink?: string
      supportsUndo?: boolean
      attachments?: Array<StagedActionAttachment>
      nestedViews?: Array<NestedActionView>
    }
  }
  toolCallView?: {
    name: string
    icons: Array<string>
    description: string
  }

  status?: ActionStatus
  unread?: boolean
  errorMessage?: string
  pendingStartTime?: string | Date
  pendingDuration?: number
  subIcon?: ReactNode
  onClick?: (actionId: string, actionType: string) => void
  onInvoke?: (actionId: string) => void
  onUndo?: (actionId: string) => void
  onDismiss?: (actionId: string) => void
  onButtonClick?: (actionId: string, button: StagedActionButton) => void
  onProgressComplete?: (actionId: string) => void

  isInvoking?: boolean
  isUndoing?: boolean
  isDismissing?: boolean
  isDismissed?: boolean

  footerLeft?: ReactNode
  footerRight?: ReactNode
  errorBannerAction?: ReactNode

  /** Replaces the standard tags/nested-views/markdown body. */
  content?: ReactNode
  contentClassName?: string

  /** Omits the standard icon-and-title header when content supplies its own titles. */
  hideHeader?: boolean

  /** Render the complete output body instead of the clamped list preview. */
  fullContent?: boolean

  longRunning?: {
    isResolved: boolean
    elapsed: string | null
    showViewProgress: boolean
    viewProgressLabel: string
    alternateLabel: string
    onViewProgress: () => void
  }

  size?: 'default' | 'compact'
}

function ActionIcon({
  stagedAction,
  toolCallView,
}: {
  stagedAction: StagedActionCellProps['stagedAction']
  toolCallView?: StagedActionCellProps['toolCallView']
}) {
  // A report card is badged with its originating skill's icon, same as the
  // actions panel / drawer / toast.
  const reportSkill = getReportOriginSkill(stagedAction)
  if (reportSkill) return <OriginSkillIcon skillName={reportSkill} />

  // The tool call's own icon outranks the shared resolver's toolSet/actionType
  // guesses, but not an explicit view icon (sentinel or URL) — getActionIcon
  // handles those first, so only fall through to it when neither applies.
  const viewIcon = stagedAction.view?.icon
  const hasViewIcon = viewIcon === 'check' || (!!viewIcon && isUrl(viewIcon))
  const toolCallIconUrl = toolCallView?.icons?.[0]
  if (!hasViewIcon && toolCallIconUrl && isUrl(toolCallIconUrl)) {
    return <img src={toolCallIconUrl} alt="" className="size-3.5 rounded-md object-contain" />
  }

  return getActionIcon(stagedAction)
}

export function StagedActionCell({
  stagedAction,
  toolCallView,
  status = 'staged',
  unread,
  errorMessage,
  subIcon,
  onClick,
  onInvoke,
  onUndo,
  onDismiss,
  onButtonClick,
  isInvoking,
  isUndoing,
  isDismissing,
  isDismissed,
  footerLeft,
  footerRight,
  errorBannerAction,
  content,
  contentClassName,
  hideHeader = false,
  fullContent = false,
  longRunning,
  size = 'default',
}: StagedActionCellProps) {
  const isKeyboard = useKeyboardInteraction()
  const [isFocused, setIsFocused] = useState(false)
  const [contentNode, setContentNode] = useState<HTMLDivElement | null>(null)
  const contentOverflows = useClampedOverflow(contentNode, fullContent)
  const [isContentExpanded, setIsContentExpanded] = useState(false)
  const {
    unsatisfiedPrerequisite,
    displayView: view,
    ctaLabel,
    warning: prerequisiteWarning,
    connectPrerequisite,
  } = useConnectionPrerequisite(stagedAction, () => onInvoke?.(stagedAction.id))
  const isPending = status === 'pending'
  const isStaged = status === 'staged'
  // report_to_user delivers a message rather than proposing work: there is
  // nothing to execute, so the invoke CTA is dropped (copy is the only action).
  const isReport = isReportToUserStagedAction(stagedAction)
  // A report is there to be read, so it can lift the full-content cap. The
  // toggle stays once expanded, where the uncapped body no longer overflows.
  const canExpandContent = isReport && fullContent && (contentOverflows || isContentExpanded)
  // An unsatisfied prerequisite outranks the view's own warning: it names the
  // one thing standing between the card and running at all.
  const warning = prerequisiteWarning ?? (isStaged ? view?.invokeWarning : undefined)
  const hasError = status === 'error'
  const hasUndoError = status === 'undo-error'
  const displayTitle = view?.title ?? stagedAction.title
  const tags = view?.tags ?? []
  const buttons = view?.buttons ?? []
  const attachments = view?.attachments ?? []
  const nestedViews = view?.nestedViews ?? []
  const hasContent =
    content != null ||
    tags.length > 0 ||
    !!view?.content ||
    nestedViews.length > 0 ||
    hasCommenterMeta(view?.contentMeta)

  const handleClick = () => {
    onClick?.(stagedAction.id, stagedAction.actionType)
  }

  const invoke = () => {
    if (view?.invokeLink) {
      window.open(view.invokeLink, '_blank')
    } else {
      onInvoke?.(stagedAction.id)
    }
    AmplitudeRenderer.trackEvent('Staged Action', {
      action: 'invoke',
      ...stagedActionEventProperties(stagedAction),
      actionType: stagedAction.actionType,
      status,
      source: 'chat',
      wasEdited: stagedAction.metadata?.userEdited === 'true',
    })
  }

  const handleInvoke = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    invoke()
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.metaKey && e.key === 'Enter' && unsatisfiedPrerequisite && !isPending && !isDismissed) {
      e.preventDefault()
      e.stopPropagation()
      connectPrerequisite()
      return
    }

    if (
      e.metaKey &&
      e.key === 'Enter' &&
      !isPending &&
      !hasUndoError &&
      !isInvoking &&
      !isDismissed &&
      !isReport &&
      onInvoke
    ) {
      e.preventDefault()
      e.stopPropagation()
      invoke()
    }
  }

  const handleUndo = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    onUndo?.(stagedAction.id)
  }

  const handleDismiss = (e: MouseEvent<HTMLButtonElement | HTMLDivElement>) => {
    e.stopPropagation()
    onDismiss?.(stagedAction.id)
  }

  const hasFooter =
    longRunning ||
    canExpandContent ||
    footerLeft ||
    footerRight ||
    !!unsatisfiedPrerequisite ||
    !!warning ||
    (!isPending && !hasUndoError && onInvoke) ||
    ((isPending || hasUndoError) && (buttons.length > 0 || (view?.supportsUndo && onUndo)))

  const defaultFooterLeft =
    isPending && view?.supportsUndo && onUndo ? (
      <Button onClick={handleUndo} size="sm" variant="ghost" disabled={isUndoing} loading={isUndoing}>
        Undo
      </Button>
    ) : warning ? (
      <ActionWarning warning={warning} />
    ) : canExpandContent ? (
      <ReportExpandToggle
        isExpanded={isContentExpanded}
        onToggle={() => setIsContentExpanded((expanded) => !expanded)}
      />
    ) : null

  // Archived actions are deprioritized: a compact single-row cell whose status
  // collapses into a small header indicator instead of a full-height footer.
  const archivedIndicator = isDismissed ? (
    longRunning ? (
      <div className="flex shrink-0 items-center gap-1.5 text-xs">
        <span className={`size-1.5 shrink-0 rounded-full ${longRunning.isResolved ? 'bg-success' : 'bg-warning'}`} />
        <span className={longRunning.isResolved ? 'text-success-subtle' : 'text-warning-subtle'}>
          {longRunning.isResolved ? 'Completed' : 'In Progress'}
        </span>
        {longRunning.elapsed && <span className="text-foreground-placeholder">{longRunning.elapsed}</span>}
      </div>
    ) : (
      <span className="text-foreground-placeholder shrink-0 text-xs">Archived</span>
    )
  ) : undefined

  const defaultFooterRight = (() => {
    if (unsatisfiedPrerequisite) {
      return <PrerequisiteCtaButton label={ctaLabel} isFocused={isFocused} onClick={connectPrerequisite} />
    }

    if ((isPending || hasUndoError) && buttons.length > 0) {
      return (
        <div className="flex items-center gap-2">
          {buttons.map((button) => (
            <Button
              key={button.link}
              size="sm"
              variant="outline"
              onClick={(e) => {
                e.stopPropagation()
                onButtonClick?.(stagedAction.id, button)
              }}
            >
              {button.label}
            </Button>
          ))}
        </div>
      )
    }

    if (!isPending && !hasUndoError && onInvoke) {
      return (
        <div className="flex items-center gap-1.5">
          {view?.content && (
            <CopyIconButton
              size="sm"
              value={view.content}
              icon="content"
              ariaLabel="Copy content"
              stopPropagation
              onCopy={() =>
                AmplitudeRenderer.trackEvent('Staged Action', {
                  action: 'copy content',
                  ...stagedActionEventProperties(stagedAction),
                  actionType: stagedAction.actionType,
                  source: 'chat',
                })
              }
            />
          )}
          {!isReport && (
            <Button
              onClick={handleInvoke}
              size="sm"
              variant="primary"
              tone={hasError ? 'danger' : 'neutral'}
              disabled={isInvoking}
              loading={isInvoking}
              left={<InvokeIcon icon={view?.invokeIcon} />}
              hotkey={isFocused ? 'cmd + enter' : undefined}
            >
              {hasError ? 'Retry' : (view?.invokeLabel ?? 'Run')}
            </Button>
          )}
        </div>
      )
    }

    return null
  })()

  return (
    <Cell
      size={isDismissed ? 'compact' : size}
      isPending={isPending && !isDismissed}
      focused={isFocused}
      onClick={onClick ? handleClick : undefined}
      onFocus={() => {
        if (isKeyboard()) setIsFocused(true)
      }}
      onBlur={() => setIsFocused(false)}
      onKeyDown={handleKeyDown}
      className={twMerge('border-transparent shadow-sm', isDismissed && 'opacity-50')}
    >
      {onDismiss && (
        <Button
          size="xs"
          variant="outline"
          shape="circle"
          aria-label="Dismiss"
          onClick={handleDismiss}
          disabled={isDismissing}
          // Opaque, since it overhangs the card's corner onto whatever sits behind it.
          className="bg-background absolute -left-[10px] -top-[10px] z-10 opacity-0 transition-opacity focus-visible:opacity-100 group-hover/cell:opacity-100"
        >
          <X className="text-foreground-secondary size-2.5" />
        </Button>
      )}

      {!hideHeader && (
        <Cell.Header
          className="min-w-0"
          indicator={archivedIndicator}
          showChevron={!isPending && (!onInvoke || isDismissed)}
        >
          {/* Archived rows are a single line: the icon centres on it instead of
              top-aligning, and the title truncates mid-word rather than line-clamping,
              which leaves the wrapped line's trailing gap before the indicator. */}
          <div className="flex min-w-0 items-center gap-2">
            <ActionIconSlot
              view={stagedAction.view}
              subIcon={isDismissed ? undefined : subIcon}
              className={isDismissed ? 'self-center' : undefined}
            >
              <ActionIcon stagedAction={stagedAction} toolCallView={toolCallView} />
            </ActionIconSlot>
            <Cell.Title className={twMerge(unread && 'text-foreground', isDismissed && 'line-clamp-none truncate')}>
              {displayTitle}
            </Cell.Title>
          </div>
        </Cell.Header>
      )}

      {hasContent && !isDismissed && (
        <div className="min-w-0 basis-full">
          <Cell.Content
            ref={setContentNode}
            className={twMerge(
              fullContent && 'select-text',
              fullContent && !isContentExpanded && 'max-h-[calc(18rem-2px)]',
              fullContent && (contentOverflows ? 'overflow-y-auto' : 'overflow-hidden'),
              contentClassName,
            )}
          >
            {content ?? (
              <>
                {tags.length > 0 && (
                  <ActionTagArea
                    tags={applyMetadataTransitions(tags, nestedViews)}
                    tagOrder={view?.tagOrder}
                    singleLine={!fullContent}
                  />
                )}
                {visibleNestedViews(nestedViews).map((nested, idx) => (
                  <NestedView
                    key={idx}
                    view={nested.view}
                    preview={!fullContent}
                    onButtonAction={(target) =>
                      AmplitudeRenderer.trackEvent('Staged Action', {
                        action: target,
                        ...stagedActionEventProperties(stagedAction),
                        actionType: stagedAction.actionType,
                        source: 'chat',
                        surface: 'nested-view',
                      })
                    }
                  />
                ))}
                <CommenterMeta contentMeta={view?.contentMeta}>
                  {view?.content && (
                    <ActionContentPreview
                      content={view.content}
                      attachments={attachments}
                      clamp={!fullContent}
                      outputTypography={fullContent}
                    />
                  )}
                </CommenterMeta>
              </>
            )}
          </Cell.Content>
        </div>
      )}

      {(hasError || hasUndoError) && (
        <Cell.Banner
          variant="danger"
          action={
            errorBannerAction ??
            (hasUndoError && onUndo ? (
              <Button variant="primary" tone="danger" size="sm" onClick={handleUndo}>
                Retry
              </Button>
            ) : undefined)
          }
        >
          {isTmuxUnavailableError(errorMessage) ? (
            <TmuxInstallHint />
          ) : (
            errorMessage || (hasUndoError ? 'Failed to undo. Try again.' : 'Something went wrong. Try again.')
          )}
        </Cell.Banner>
      )}

      {/* min-w-0 down to the left slot so a truncating child (see ActionWarning)
          ellipsizes instead of pushing the buttons off the cell's right edge. */}
      {!isDismissed && hasFooter && (
        <Cell.Footer className="min-w-0">
          <div className="flex w-full min-w-0 items-center justify-between gap-1.5">
            <div className="flex min-w-0 flex-1 items-center">{footerLeft ?? defaultFooterLeft}</div>
            <div className="flex shrink-0 items-center gap-2">{footerRight ?? defaultFooterRight}</div>
          </div>
        </Cell.Footer>
      )}
    </Cell>
  )
}
